import React, { memo, useState } from "react";
import { FieldArray, FormikProps, useFormikContext } from "formik";
import { Add, Delete, ExpandMore, SpeakerPhone } from "@mui/icons-material";
import { TerminalApp, Data } from "../../../../../store/terminal/types";
import {
  Grid,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
} from "@mui/material";
import pageStyles from "../../../../pageStyles";
import SectionWrapper from "../../../../../common/pageSections/SectionWrapper";
import Confirm from "../../../../../common/confirm/Confirm";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import ApplicationForm from "./applicationForm";
import Edit from "@mui/icons-material/Edit";

interface Props {
  isUpdateAllowed?: boolean;
  shouldHideField: (fieldName: string) => boolean;
}

const ApplicationComponent = ({ isUpdateAllowed, shouldHideField }: Props) => {
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;

  const styles = pageStyles();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(0);
  const [expand, setExpand] = useState(
    new Array(values?.attributes?.Applications?.length).fill(false)
  );

  const applicationObj = {
    VersionString: "",
    UpdateGroup: "",
    Variant: null,
    AppName: "",
    isEditAble: true,
  };
  const applicationOptions =
    useSelector((state: ApplicationState) => state.myAxis?.applications) ?? [];
  const versions =
    useSelector((state: ApplicationState) => state.myAxis?.versions) ?? {};
  const myAxis = useSelector((state: ApplicationState) => state.myAxis);

  if (!myAxis) return null;

  // Check if Applications section should be hidden
  if (shouldHideField("attributes.Applications")) {
    return null;
  }

  return (
    <SectionWrapper title="Applications" isChild isWhiteBG>
      <FieldArray
        name="attributes.Applications"
        render={(arrayHelpers) => {
          return (
            <>
              {values?.attributes?.Applications?.map(
                (application: TerminalApp, index: number) => {
                  const appName = application.AppName;
                  return (
                    <Accordion
                      key={index}
                      className={styles.Accordion}
                      onChange={(e, expanded) => {
                        expand[index] = expanded;
                        setExpand(expand);
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls={`Application_${index}_content`}
                        id={`Application_${index}_header`}
                      >
                        <Box className={styles.processorTitleWrapper}>
                          <Typography className={styles.processorTitle}>
                            <SpeakerPhone style={{ marginRight: "5px" }} />
                            {appName || `Application ${index + 1}`}
                          </Typography>
                          <div>
                            <Button
                              style={{ marginRight: "10px" }}
                              color="primary"
                              variant="outlined"
                              onClick={(e) => {
                                setFieldValue(
                                  `attributes.Applications[${index}].isEditAble`,
                                  true
                                );
                                if (expand[index]) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                              size="small"
                              startIcon={<Edit />}
                              disabled={!isUpdateAllowed}
                            >
                              Edit
                            </Button>
                            <Button
                              color="secondary"
                              variant="outlined"
                              onClick={(e) => {
                                setDeleteItemId(index);
                                setShowConfirm(true);
                              }}
                              size="small"
                              startIcon={<Delete />}
                              disabled={!isUpdateAllowed}
                            >
                              Delete
                            </Button>
                          </div>
                        </Box>
                        <Confirm
                          isOpen={showConfirm}
                          onCancel={() => setShowConfirm(false)}
                          onConfirm={() => {
                            arrayHelpers.remove(deleteItemId);
                            setShowConfirm(false);
                          }}
                          title="Delete Application"
                          icon="danger"
                          confirmLabel="Delete"
                          contentText={`You are about to delete an Application`}
                        />
                      </AccordionSummary>
                      <AccordionDetails
                        style={{
                          background: "#fff",
                          borderTop: "1px solid #efefef",
                        }}
                      >
                        <ApplicationForm
                          versions={versions}
                          index={index}
                          application={application}
                          applicationOptions={applicationOptions}
                          isUpdateAllowed={isUpdateAllowed}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                }
              )}

              <Grid item xs={12}>
                <Box textAlign="center">
                  <Button
                    color="primary"
                    variant="contained"
                    size="medium"
                    endIcon={<Add />}
                    onClick={() => {
                      arrayHelpers.push(applicationObj);
                    }}
                    style={{ marginTop: "15px" }}
                    disabled={!isUpdateAllowed}
                  >
                    Create Application
                  </Button>
                </Box>
              </Grid>
            </>
          );
        }}
      />
    </SectionWrapper>
  );
};
export default memo(ApplicationComponent);
